/* eslint-disable @typescript-eslint/no-explicit-any */
import { EventEmitter, useSubscription, StatefulEmitter } from '@apia/util';
import { FC, ReactNode } from 'react';
import { TOption } from '../components';
import {
  TFocusRetriever,
  TValidationFunction,
  TValidationRules,
} from '../types';
import { Box } from '@apia/theme';
import { Collector } from './Collector';

export abstract class CollectorField<
  ValueType = any,
  FieldProps = any,
> extends EventEmitter<{
  focusFunctionUpdate: TFocusRetriever;
  functionUpdate: TValidationFunction<ValueType>;
  optionsUpdate: TOption[];
  propsUpdate: Partial<FieldProps>;
  rulesUpdate: TValidationRules;
}> {
  /**
   * Este es el componente de implementación de cada campo particular
   */
  abstract Component: FC;
  abstract type: string;

  #emitter = new StatefulEmitter<{
    isHidden: boolean;
  }>({
    isHidden: false,
  });

  constructor(
    public readonly name: string,
    protected label: ReactNode,
    properties?: Partial<FieldProps>,
  ) {
    super();

    if (properties) {
      this.properties = properties;
    }
  }

  public collector: Collector = {} as Collector;

  isHidden = () => this.#emitter.getState('isHidden');
  setIsHidden = (isHidden: boolean) => {
    this.#emitter.emit('isHidden', isHidden);
  };

  protected properties: Partial<FieldProps> = {};
  setProperties = (newProps: Partial<FieldProps>) => {
    this.properties = newProps;
    this.emit('propsUpdate', { ...this.properties });
  };

  protected useProperties = () => {
    return useSubscription<Partial<FieldProps>>({
      getInitialState: () => this.properties,
      makeSubscription: (setProps) => this.on('propsUpdate', setProps),
    });
  };

  /**
   * Este componente se utiliza para controlar propiedades comunes a todos los
   * campos.
   */
  SuperComponent = () => {
    if (this.#emitter.useState('isHidden')) return null;

    return (
      <Box className={`collector__field ${this.type}`}>
        <this.Component />
      </Box>
    );
  };
}
